/**
 * Découpage de la France en tuiles d'image satellite, et conversion des
 * pixels en coordonnées.
 *
 * Tout est calculé ici, sans toucher au réseau ni au canvas, pour que la
 * partie la plus délicate du balayage soit vérifiable par des tests.
 */
import { SCAN_GRID, SCAN_TILE_PX } from './constants';
import { FRANCE_BOX } from './france';
import { project, unproject } from './mercator';
import type { LatLon } from './types';

/** Une emprise en mètres projetés, telle qu'attendue par une requête WMS. */
export interface TileBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Le paramètre `bbox` d'une requête WMS, dans l'ordre imposé par la norme. */
export function toBboxParam(box: TileBox): string {
  return `${box.minX},${box.minY},${box.maxX},${box.maxY}`;
}

/**
 * La grille FIXE qui couvre la France métropolitaine.
 *
 * Fixe, et jamais calée sur la fenêtre affichée : sinon la résolution change
 * à chaque zoom et les zones se redessinent différemment à chaque
 * déplacement de la carte. C'est ce qui les faisait bouger.
 */
export function franceTileGrid(grid: number = SCAN_GRID): TileBox[] {
  const southWest = project(FRANCE_BOX.south, FRANCE_BOX.west);
  const northEast = project(FRANCE_BOX.north, FRANCE_BOX.east);
  const stepX = (northEast.x - southWest.x) / grid;
  const stepY = (northEast.y - southWest.y) / grid;

  const boxes: TileBox[] = [];
  for (let row = 0; row < grid; row += 1) {
    for (let col = 0; col < grid; col += 1) {
      boxes.push({
        minX: southWest.x + col * stepX,
        minY: southWest.y + row * stepY,
        maxX: southWest.x + (col + 1) * stepX,
        maxY: southWest.y + (row + 1) * stepY,
      });
    }
  }
  return boxes;
}

/**
 * Position géographique du centre d'un pixel.
 *
 * L'axe vertical est inversé : la ligne 0 d'une image est son bord NORD,
 * donc la plus grande ordonnée projetée. Oublier cette inversion place tous
 * les feux en miroir par rapport à leur vraie latitude.
 */
export function pixelToLatLon(
  box: TileBox,
  pixelX: number,
  pixelY: number,
  tilePx: number = SCAN_TILE_PX,
): LatLon {
  const scaleX = (box.maxX - box.minX) / tilePx;
  const scaleY = (box.maxY - box.minY) / tilePx;
  return unproject(
    box.minX + (pixelX + 0.5) * scaleX,
    box.maxY - (pixelY + 0.5) * scaleY,
  );
}

/**
 * Résolution au sol d'une tuile, en mètres par pixel.
 *
 * L'emprise de la France n'est pas carrée alors que les tuiles le sont : les
 * deux axes n'ont donc pas exactement la même résolution, environ 842 m par
 * pixel en longitude contre 814 en latitude. Les conversions en tiennent
 * compte séparément, cette fonction sert à le vérifier.
 */
export function tileResolutionMeters(
  box: TileBox,
  tilePx: number = SCAN_TILE_PX,
): { x: number; y: number } {
  return {
    x: (box.maxX - box.minX) / tilePx,
    y: (box.maxY - box.minY) / tilePx,
  };
}

/**
 * Un pixel est-il une détection thermique ?
 *
 * Les couches GIBS sont des images presque entièrement transparentes, semées
 * de taches rouges. Le seuil sur l'alpha à 40 attrape aussi les bords
 * lissés, sans quoi les petits foyers perdraient la moitié de leur étendue.
 */
export function isFirePixel(red: number, green: number, blue: number, alpha: number): boolean {
  return alpha > 40 && red > 150 && green < 140 && blue < 140;
}
